import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { transferRequisitionsApi, warehousesApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M2-TR-03 — Transfer requisition detail with state actions (Submit/Approve/Reject)
 * and a Convert dialog that asks the operator to pick the source warehouse and a batch id
 * per line. On success, navigates to the new TransferOrder draft.
 */
export function TransferRequisitionDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canCreate  = useAuth(s => s.hasPermission("warehouse.transfer.create"));
  const canApprove = useAuth(s => s.hasPermission("warehouse.transfer.approve"));

  const [openAction, setOpenAction] = useState<null | "submit" | "approve" | "reject" | "convert">(null);
  const [reason, setReason] = useState("");
  const [sourceWarehouseId, setSourceWarehouseId] = useState("");
  const [batchByLine, setBatchByLine] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ["transfer-requisition", id],
    queryFn: () => transferRequisitionsApi.get(id),
    enabled: !!id,
  });
  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
    enabled: openAction === "convert",
  });

  const stateMutation = useMutation({
    mutationFn: async () => {
      if (!query.data) return;
      const rv = query.data.rowVersion;
      switch (openAction) {
        case "submit":  await transferRequisitionsApi.submit(id, rv); break;
        case "approve": await transferRequisitionsApi.approve(id, rv); break;
        case "reject":  await transferRequisitionsApi.reject(id, reason, rv); break;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transfer-requisition", id] });
      qc.invalidateQueries({ queryKey: ["transfer-requisitions"] });
      setOpenAction(null); setReason("");
    },
  });

  const convertMutation = useMutation({
    mutationFn: () => transferRequisitionsApi.convert(id, sourceWarehouseId, batchByLine),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["transfer-requisition", id] });
      navigate(`/warehouse/transfers/${r.transferOrderId}`);
    },
  });

  if (query.isLoading || !query.data) return <CircularProgress />;
  const r = query.data;
  const allLinesHaveBatch = r.lines.every(l => batchByLine[l.id]?.trim());

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{r.documentNo}</Typography>
          <Chip label={r.status} color={STATUS_COLOR[r.status] ?? "default"} size="small" />
          {r.convertedToTransferOrderId && (
            <Chip
              size="small" color="success"
              label="Converted →"
              component={RouterLink as any}
              to={`/warehouse/transfers/${r.convertedToTransferOrderId}`}
              clickable
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          {r.status === "Draft" && canCreate && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOpenAction("submit")}>Submit</Button>
          )}
          {r.status === "Submitted" && canApprove && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckIcon />}
                onClick={() => setOpenAction("approve")}>Approve</Button>
              <Button color="warning" startIcon={<CancelIcon />}
                onClick={() => setOpenAction("reject")}>Reject</Button>
            </>
          )}
          {r.status === "Approved" && !r.convertedToTransferOrderId && canCreate && (
            <Button variant="contained" startIcon={<SwapHorizIcon />}
              onClick={() => setOpenAction("convert")}>Convert → TransferOrder</Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack><Typography variant="caption" color="text.secondary">Requesting warehouse</Typography>
            <Typography>{r.requestingWarehouseName}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Suggested source</Typography>
            <Typography>{r.suggestedSourceWarehouseName ?? "—"}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Needed by</Typography>
            <Typography>{r.neededByDate ? new Date(r.neededByDate).toLocaleDateString() : "—"}</Typography></Stack>
        </Stack>
        {r.reason && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Reason</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{r.reason}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Requested qty</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {r.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell align="right">{l.requestedQty}</TableCell>
                <TableCell>{l.reason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/warehouse/transfer-requisitions")}>Back to list</Button>

      {/* Simple confirm/reject dialog */}
      <Dialog open={openAction !== null && openAction !== "convert"}
        onClose={() => { setOpenAction(null); setReason(""); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          {openAction === "submit" && "Submit requisition"}
          {openAction === "approve" && "Approve requisition"}
          {openAction === "reject" && "Reject requisition"}
        </DialogTitle>
        <DialogContent>
          {openAction === "reject" && (
            <TextField label="Reason *" fullWidth multiline minRows={3} sx={{ mt: 1 }}
              value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          {(openAction === "submit" || openAction === "approve") && (
            <DialogContentText>Confirm the state transition.</DialogContentText>
          )}
          {stateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(stateMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAction(null); setReason(""); }}>Cancel</Button>
          <Button variant="contained"
            disabled={(openAction === "reject" && !reason) || stateMutation.isPending}
            onClick={() => stateMutation.mutate()}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={openAction === "convert"} onClose={() => setOpenAction(null)} maxWidth="md" fullWidth>
        <DialogTitle>Convert requisition → TransferOrder</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pick the source warehouse and a batch id per line; the system creates a Draft TransferOrder
            with these lines linked back to the requisition.
          </DialogContentText>
          <TextField select label="Source warehouse *" fullWidth sx={{ mb: 2 }}
            value={sourceWarehouseId} onChange={(e) => setSourceWarehouseId(e.target.value)}>
            <MenuItem value="">— pick source —</MenuItem>
            {warehouses.data?.items.filter(w => w.id !== r.requestingWarehouseId).map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Batch id *</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {r.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.itemName} <Typography variant="caption" color="text.secondary">{l.sku}</Typography></TableCell>
                  <TableCell align="right">{l.requestedQty}</TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth placeholder="Batch UUID"
                      value={batchByLine[l.id] ?? ""}
                      onChange={(e) => setBatchByLine(prev => ({ ...prev, [l.id]: e.target.value }))} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {convertMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(convertMutation.error)}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAction(null)}>Cancel</Button>
          <Button variant="contained"
            disabled={!sourceWarehouseId || !allLinesHaveBatch || convertMutation.isPending}
            onClick={() => convertMutation.mutate()}>Convert</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
